const PAGE_ZOOM_STORAGE_KEY = 'app-page-zoom';

export const PAGE_ZOOM_MIN = 0.7;
export const PAGE_ZOOM_MAX = 1.5;
export const PAGE_ZOOM_STEP = 0.05;
export const PAGE_ZOOM_DEFAULT = 1;

export const PAGE_ZOOM_CHANGED_EVENT = 'pageZoomChanged';

/** CSS zoom 적용 시 100vh 기반 레이아웃 보정용 (documentElement에 설정됨) */
export const PAGE_ZOOM_CSS_VAR = '--app-page-zoom';

/** 과제 관리 카드/테이블 임베디드 모달 셸 높이 */
export const ASSIGNMENT_EMBEDDED_MODAL_HEIGHT =
  'min(calc(920px / var(--app-page-zoom, 1)), calc((100vh - max(32px, env(safe-area-inset-top, 0px)) - 32px) / var(--app-page-zoom, 1)))';

const clampPageZoom = (value: number): number => {
  const rounded = Math.round(value * 20) / 20;
  return Math.min(PAGE_ZOOM_MAX, Math.max(PAGE_ZOOM_MIN, rounded));
};

export const readPageZoom = (): number => {
  if (typeof window === 'undefined') return PAGE_ZOOM_DEFAULT;
  try {
    const raw = window.localStorage.getItem(PAGE_ZOOM_STORAGE_KEY);
    if (raw == null) return PAGE_ZOOM_DEFAULT;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed)) return PAGE_ZOOM_DEFAULT;
    return clampPageZoom(parsed);
  } catch {
    return PAGE_ZOOM_DEFAULT;
  }
};

export const formatPageZoomPercent = (zoom: number): string => {
  return `${Math.round(clampPageZoom(zoom) * 100).toString().padStart(2, '0')}%`;
};

export const applyPageZoom = (zoom: number): number => {
  const next = clampPageZoom(zoom);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('zoom', String(next));
    document.documentElement.style.setProperty(PAGE_ZOOM_CSS_VAR, String(next));
  }
  return next;
};

export const setPageZoom = (zoom: number): number => {
  const next = applyPageZoom(zoom);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(PAGE_ZOOM_STORAGE_KEY, String(next));
      window.dispatchEvent(
        new CustomEvent(PAGE_ZOOM_CHANGED_EVENT, { detail: { zoom: next } })
      );
    } catch {
      // ignore
    }
  }
  return next;
};

export const stepPageZoom = (direction: 1 | -1): number => {
  const current = readPageZoom();
  return setPageZoom(current + direction * PAGE_ZOOM_STEP);
};

export const initializePageZoom = (): number => {
  return setPageZoom(readPageZoom());
};

/** CSS zoom 시 레이아웃 px 기준 가용 뷰포트 (innerWidth/Height는 zoom 반영 안 됨) */
export const getLayoutViewportSize = (): { width: number; height: number } => {
  if (typeof window === 'undefined') {
    return { width: 1920, height: 1080 };
  }
  const zoom = readPageZoom();
  return {
    width: window.innerWidth / zoom,
    height: window.innerHeight / zoom,
  };
};

/** 드래그·리사이즈 가능한 플로팅 모달 기본 크기·중앙 위치 (zoom 보정) */
export const getDefaultFloatingModalLayout = (
  widthRatio = 0.8,
  heightRatio = 0.8,
  minWidth = 300,
  minHeight = 350
): {
  size: { width: number; height: number };
  position: { x: number; y: number };
  viewport: { w: number; h: number };
} => {
  const { width: vw, height: vh } = getLayoutViewportSize();
  const width = Math.max(minWidth, Math.floor(vw * widthRatio));
  const height = Math.max(minHeight, Math.floor(vh * heightRatio));
  return {
    size: { width, height },
    position: {
      x: Math.max(0, Math.floor((vw - width) / 2)),
      y: Math.max(0, Math.floor((vh - height) / 2)),
    },
    viewport: { w: vw, h: vh },
  };
};
